// Calendar API — typed wrappers for /api/v1/calendar/* (W6).
//
// Mirrors the anniversary.ts pattern: openapi-fetch + ApiError helper +
// payload types (per frontend/AGENTS.md §5) — now aliases onto the
// generated contract where identical to it (`schema.ts`), hand-written where not,
// each with a recorded reason in `schema-parity.test.ts`.

import { api as openapi } from "./client";
import { ApiError } from "./pair";
import { assertResponseBody } from "./emptyBody";
import type {
  CalendarEventOut as SchemaCalendarEventOut,
  CycleCellStateOut as SchemaCycleCellStateOut,
  CycleHistoryOut as SchemaCycleHistoryOut,
  CycleLogOut as SchemaCycleLogOut,
  CycleLogUpsert as SchemaCycleLogUpsert,
  CyclePhaseOut as SchemaCyclePhaseOut,
  CycleProjectionOut as SchemaCycleProjectionOut,
  CycleSettingsOut as SchemaCycleSettingsOut,
  CycleSettingsUpdate as SchemaCycleSettingsUpdate,
  InferenceBandOut as SchemaInferenceBandOut,
  OverlapOut as SchemaOverlapOut,
  SharedSegmentOut as SchemaSharedSegmentOut,
} from "./schema";
import { API_REQUEST_FAILED } from "../i18n/errors";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

interface CallOptions {
  body?: unknown;
  headers?: Record<string, string>;
  /** openapi-fetch reads query params from `params.query` — see diary.ts. */
  params?: { query: Record<string, unknown> };
  /**
   * Set `false` only on a route whose OpenAPI contract declares no 2xx body
   * (every one of them answers `204`). A 2xx that arrives empty on any other
   * route is a broken server and must reach `isError` — see `emptyBody.ts`.
   */
  expectBody?: boolean;
}

async function call<T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: CallOptions = {},
): Promise<{ response: Response; data: T | null; error?: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (result.data as any) ?? null;
  // A bodyless 2xx where the contract declares a body arrives as `null` and
  // every `return res.data as T` below would ship it to a screen. Guard the
  // one funnel rather than each component — see `emptyBody.ts`.
  if (result.response?.ok)
    assertResponseBody(result.response, result.data, opts.expectBody !== false);
  // W39.79. `error` is openapi-fetch's **already-parsed** body for a non-2xx.
  // Without it `apiError` can only try to re-read a body the client consumed.
  return { response: result.response, data, error: (result as { error?: unknown }).error };
}

async function apiError(
  res: { response: Response; data: unknown; error?: unknown },
  fallbackCode: string,
): Promise<ApiError> {
  // W39.81 — read openapi-fetch's already-parsed body first. The `Response` in
  // `res.response` has had its body consumed by the client
  // (`dist/index.js:244`), so `clone().json()` cannot read it and every calendar
  // error fell through to the fallback. See `album.ts` for the full account,
  // including why W39.79's "repo-wide" claim was five modules short.
  let body: unknown = res.error ?? null;
  if (body === null) {
    try {
      body = await res.response.clone().json();
    } catch {
      body = null;
    }
  }
  const err = (
    body as {
      error?: { code?: string; message?: string; details?: unknown };
    } | null
  )?.error;
  return new ApiError(
    res.response.status,
    err?.code ?? fallbackCode,
    err?.message ?? API_REQUEST_FAILED,
  );
}

// ----- Schemas (mirror backend app/modules/calendar/schemas.py) -------------

/** Categories that materialise as a CalendarEvent row. */
export type OwnedCategory = "leave" | "trip" | "cycle" | "manual";
/** Categories that only ever exist as a read-time projection. */
export type ProjectionCategory = "anniversary" | "wish" | "diary";
export type CalendarCategory = OwnedCategory | ProjectionCategory;

export type EventVisibility = "shared" | "private";
export type OwnerToken = "me" | "partner";
export type OwnerFilter = "all" | "mine" | "partner";
/** An item's owner is always one of the two people — never the filter value. */
export type ItemOwner = "me" | "partner";

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type CalendarEvent = SchemaCalendarEventOut;

/**
 * One thing on the calendar. `id` is null for projections (anniversary / wish /
 * diary) because they are not stored — the frontend uses `source_type` +
 * `source_id` to deep-link instead (Q21).
 */
export interface CalendarItem {
  id: string | null;
  category: CalendarCategory;
  title: string;
  owner: ItemOwner;
  all_day: boolean;
  time: string | null;
  count: number;
  /** Carried per item, not per cell: a multi-day event appears in many cells. */
  start_date: string;
  end_date: string | null;
  source_type: string | null;
  source_id: string | null;
  recorded_by: string | null;
  recorded_by_name: string | null;
}

/**
 * 刻意手寫：`items` 是本地的 `CalendarItem`，其 `category` 已收斂成
 * `CalendarCategory`，而 contract 標成裸 `string`。月曆 grid 會直接賦值，
 * 所以 alias 這一層編譯不過 —— 而且會跟上面兩行宣告的 `CalendarItem` 矛盾。
 * 見 `schema-parity.test.ts`。
 */
export interface CalendarDayCell {
  date: string;
  in_month: boolean;
  is_today: boolean;
  items: CalendarItem[];
  overflow_count: number;
  inference: boolean;
  /**
   * W58.1 — 這一格在週期裡是什麼狀態。
   *
   * **一個物件講完一件事**：實際記錄的經期（`actual`）與推算的經期
   * （`predicted`）是兩件事，必須分得開 —— 這正是 Meetyou 的月曆
   * （紅色 = 真實、淡色 = 預測）建立在上的區別。
   *
   * `null` = 系統對這一天一無所知（沒有記錄、沒有推算、或預測被關掉）。
   */
  cycle_state: CycleCellState | null;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type CycleCellState = SchemaCycleCellStateOut;

/**
 * 模型裡的五個階段（`CycleProjectionOut.phase` 的收斂版）。
 *
 * `follicular`（卵泡期）與 `luteal`（黃體期）合起來就是**安全期** ——
 * 不在經期、也不在易孕期 / 排卵期的日子。
 */
export type CyclePhaseKind =
  | "menstrual"
  | "follicular"
  | "fertile"
  | "ovulation"
  | "luteal";

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type CyclePhase = SchemaCyclePhaseOut;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type CycleProjection = SchemaCycleProjectionOut;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type CycleLog = SchemaCycleLogOut;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type CycleLogInput = SchemaCycleLogUpsert;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type CycleSettings = SchemaCycleSettingsOut;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type CycleSettingsInput = SchemaCycleSettingsUpdate;

/** 流量（flow）。`spotting` = 點滴。 */
export const FLOW_OPTIONS = ["spotting", "light", "medium", "heavy"] as const;

/** 痛經程度。 */
export const CRAMP_OPTIONS = ["none", "mild", "moderate", "severe"] as const;

export type FlowValue = (typeof FLOW_OPTIONS)[number];
export type CrampValue = (typeof CRAMP_OPTIONS)[number];

/** 繁體中文標籤（AGENTS.md §3 #14）。 */
export const FLOW_LABEL: Record<FlowValue, string> = {
  spotting: "點滴",
  light: "少",
  medium: "中",
  heavy: "多",
};

export const CRAMP_LABEL: Record<CrampValue, string> = {
  none: "無",
  mild: "輕微",
  moderate: "中度",
  severe: "嚴重",
};

/** 症狀標籤（後端的白名單是權威，這裡是給 UI 用的同一份）。 */
export const SYMPTOM_OPTIONS = [
  "水腫",
  "疲倦",
  "頭痛",
  "情緒波動",
  "腰痠",
  "乳房脹痛",
  "食慾增加",
  "失眠",
] as const;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type SharedSegment = SchemaSharedSegmentOut;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type InferenceBand = SchemaInferenceBandOut;

/**
 * 推算的依據（W58.2）。
 *
 * - `low` / `medium` / `high` —— **從你的資料算出來**的信心。
 * - `partial` —— 記錄不足（2 筆），用了記錄裡的那 1 個間隔。
 * - `preset` —— 連間隔都不知道（1 筆），用了你設定的預設週期長度。
 *
 * **後兩者必須被講出來**：一個沒有說明自己怎麼算出來的預測，使用者沒辦法
 * 判斷該不該信 —— 而那正是「寧可沒有，不要錯的」這條規則在新語言下的樣子。
 */
export type CycleConfidence = "low" | "medium" | "high" | "partial" | "preset";

export interface CycleInference {
  average_interval_days: number;
  last_start: string;
  predicted_start: string;
  duration_days: number;
  confidence: CycleConfidence;
  confidence_label: string;
  sample_size: number;
  /** W58.0 — 同一份推算攤開成階段與日期。推算不存在時是 `null`。 */
  projection: CycleProjection | null;
  /** W58.1 — 這個人有沒有把預測關掉。 */
  predictions_enabled: boolean;
}

export interface CalendarMonth {
  year: number;
  month: number;
  weeks: CalendarDayCell[][];
  shared_segments: SharedSegment[];
  inference: InferenceBand[];
  cycle_inference: CycleInference | null;
  /** W58.1 — 這個月裡的每日經期紀錄。 */
  cycle_logs: CycleLog[];
  meta: { max_per_day: number; truncated_days: number };
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type CalendarOverlap = SchemaOverlapOut;

export interface DayCycleInference {
  last_start: string;
  interval_days: number;
  predicted_start: string;
  confidence: "low" | "medium" | "high";
  /** W58.0 — 該日的階段與它是週期第幾天。 */
  phase: CyclePhaseKind | null;
  phase_label: string | null;
  cycle_day: number | null;
  /** W58.1 — 安全期（不在經期、也不在易孕期 / 排卵期）。 */
  safe: boolean;
}

export interface CalendarDay {
  date: string;
  weekday: string;
  summary: string;
  overlap: CalendarOverlap[];
  items: CalendarItem[];
  cycle_inference: DayCycleInference | null;
  /** W58.1 — 這一天的經期伴隨紀錄（流量 / 痛經 / 症狀）。 */
  cycle_log: CycleLog | null;
  /**
   * W58.4 — 這一對的週期是**誰的身體**（相對於呼叫者）。
   *
   * 由後端判定（最近一筆週期事件的歸屬人），不是前端選的 —— 讓前端選會讓
   * 男方按「我」時記成自己的身體，**遮住**女方同一天的真實紀錄。
   */
  cycle_owner: OwnerToken | null;
  /**
   * W58.5 — 這一天的週期狀態，**與月曆格同一個形狀**（`CycleCellState`）。
   *
   * `actual` 是**事實**（有一筆記錄的經期事件覆蓋今天），而 `cycle_inference`
   * 講的是**推算** —— 每日紀錄的表單要依 `actual` 決定要不要問流量與痛經
   * （ADR-0211）。`null` = 系統對這一天一無所知。
   */
  cycle_state: CycleCellState | null;
}

// Contract alias — six required fields, all primitives, exact match on both
// sides. `schema-parity.test.ts` §VERIFIED_IDENTICAL fails to compile if the
// contract ever stops agreeing.
export type CycleHistoryEntry = SchemaCycleHistoryOut;

export interface CalendarCycle {
  average_interval_days: number | null;
  last_start: string | null;
  predicted_start: string | null;
  confidence: CycleConfidence | null;
  /** W58.2 — 給使用者看的說明（「把握低（基於預設）」）。 */
  confidence_label: string | null;
  sample_size: number;
  history: CycleHistoryEntry[];
  /** W58.0 — 當前週期的階段投影。樣本不足時是 `null`。 */
  projection: CycleProjection | null;
  /** W58.1 — 推算的經期開始日已經過去幾天了（超過 3 天就該提醒）。 */
  days_late: number;
  /** W58.1 — 這個人的週期設定。 */
  settings: CycleSettings;
  predictions_enabled: boolean;
  /**
   * W58.4 — 這一對的週期是**誰的身體**（相對於呼叫者）。`null` = 這一對
   * 還沒有任何週期的痕跡。
   */
  cycle_owner: OwnerToken | null;
}

export interface MonthQuery {
  year: number;
  month: number;
  categories?: CalendarCategory[];
  owner?: OwnerFilter;
  maxPerDay?: number;
}

/** Repeating a query key per value is what makes the two filters work at all. */
function queryString(query: MonthQuery): string {
  const p = new URLSearchParams();
  p.set("year", String(query.year));
  p.set("month", String(query.month));
  for (const c of query.categories ?? []) p.append("categories", c);
  if (query.owner && query.owner !== "all") p.set("owner", query.owner);
  if (query.maxPerDay != null) p.set("max_per_day", String(query.maxPerDay));
  return p.toString();
}

// ----- reads ---------------------------------------------------------------

export async function getMonth(query: MonthQuery): Promise<CalendarMonth> {
  const res = await call<CalendarMonth>("GET", `/api/v1/calendar/month?${queryString(query)}`);
  if (!res.response.ok) throw await apiError(res, "CALENDAR_MONTH_FAILED");
  if (!res.data) throw new ApiError(res.response.status, "CALENDAR_MONTH_EMPTY", "月視圖沒有資料");
  return res.data;
}

export interface DayQuery {
  date: string;
  categories?: CalendarCategory[];
  owner?: OwnerFilter;
}

export async function getDay(query: DayQuery): Promise<CalendarDay> {
  const p = new URLSearchParams();
  p.set("date", query.date);
  for (const c of query.categories ?? []) p.append("categories", c);
  if (query.owner && query.owner !== "all") p.set("owner", query.owner);
  const res = await call<CalendarDay>("GET", `/api/v1/calendar/day?${p.toString()}`);
  if (!res.response.ok) throw await apiError(res, "CALENDAR_DAY_FAILED");
  if (!res.data) throw new ApiError(res.response.status, "CALENDAR_DAY_EMPTY", "日詳情沒有資料");
  return res.data;
}

export async function getCycle(): Promise<CalendarCycle> {
  const res = await call<CalendarCycle>("GET", "/api/v1/calendar/cycle");
  if (!res.response.ok) throw await apiError(res, "CALENDAR_CYCLE_FAILED");
  if (!res.data) throw new ApiError(res.response.status, "CALENDAR_CYCLE_EMPTY", "週期沒有資料");
  return res.data;
}

/**
 * One event, for `/calendar/events/:id`.
 *
 * The backend answers **404** (not 403) for a `private` event the caller does
 * not own — a 403 would confirm it exists, which is itself information the
 * partner is not entitled to. So this throws `ApiError(404, …)` in that case
 * and the page must treat 404 as "not available" rather than "try again".
 */
export async function getEvent(id: string): Promise<CalendarEvent> {
  const res = await call<CalendarEvent>("GET", `/api/v1/calendar/events/${id}`);
  if (!res.response.ok) throw await apiError(res, "CALENDAR_EVENT_FAILED");
  if (!res.data) {
    throw new ApiError(res.response.status, "CALENDAR_EVENT_EMPTY", "事件沒有資料");
  }
  return res.data;
}

// ----- writes --------------------------------------------------------------

/**
 * Detach the manual source link (Q21).
 *
 * Its own endpoint rather than `updateEvent({source_type: null})`: the PATCH
 * body is serialised with nulls omitted, so a null can never reach the server
 * and would be indistinguishable from a no-op. An explicit verb cannot.
 */
export async function unlinkEvent(id: string): Promise<CalendarEvent> {
  const res = await call<CalendarEvent>("POST", `/api/v1/calendar/events/${id}/unlink`);
  if (!res.response.ok) throw await apiError(res, "CALENDAR_UNLINK_FAILED");
  if (!res.data) {
    throw new ApiError(res.response.status, "CALENDAR_UNLINK_EMPTY", "取消關聯失敗");
  }
  return res.data;
}

export interface EventCreateInput {
  category: OwnedCategory;
  title: string;
  start_date: string;
  end_date?: string | null;
  all_day?: boolean;
  owner_id: OwnerToken;
  visibility: EventVisibility;
  source_type?: "anniversary" | "wish" | "diary" | "album" | null;
  source_id?: string | null;
  note?: string | null;
}

export async function createEvent(input: EventCreateInput): Promise<CalendarEvent> {
  const res = await call<CalendarEvent>("POST", "/api/v1/calendar/events", {
    body: input,
  });
  if (!res.response.ok) throw await apiError(res, "CALENDAR_CREATE_FAILED");
  if (!res.data) throw new ApiError(res.response.status, "CALENDAR_CREATE_EMPTY", "建立事件失敗");
  return res.data;
}

export type EventUpdateInput = Partial<
  Omit<EventCreateInput, "all_day" | "source_type" | "source_id">
> & {
  source_type?: "anniversary" | "wish" | "diary" | "album" | null;
  source_id?: string | null;
};

export async function updateEvent(id: string, input: EventUpdateInput): Promise<CalendarEvent> {
  const res = await call<CalendarEvent>("PATCH", `/api/v1/calendar/events/${id}`, {
    body: input,
  });
  if (!res.response.ok) throw await apiError(res, "CALENDAR_UPDATE_FAILED");
  if (!res.data) throw new ApiError(res.response.status, "CALENDAR_UPDATE_EMPTY", "更新事件失敗");
  return res.data;
}

export async function deleteEvent(id: string): Promise<void> {
  const res = await call<null>("DELETE", `/api/v1/calendar/events/${id}`, { expectBody: false });
  if (!res.response.ok) throw await apiError(res, "CALENDAR_DELETE_FAILED");
}

/**
 * Undo a soft delete, inside the 7-day window (W39.1 R1 / ADR-0080).
 *
 * The delete is reversible in the data but was not in the product until this
 * route existed — diary, anniversary and wish each had one; calendar did not,
 * so the R1 undo bar would have offered a 「復原」 that did nothing. Added in
 * the same change as the undo bar, per AGENTS.md §3 #2 (contract first).
 */
export async function restoreEvent(id: string): Promise<CalendarEvent> {
  const res = await call<CalendarEvent>("POST", `/api/v1/calendar/events/${id}/restore`);
  if (!res.response.ok) throw await apiError(res, "CALENDAR_RESTORE_FAILED");
  if (!res.data) throw new ApiError(res.response.status, "CALENDAR_RESTORE_EMPTY", "還原事件失敗");
  return res.data;
}

/** Manual source link (Q21). There is deliberately no date-range link. */
export async function linkEvent(
  id: string,
  sourceType: "anniversary" | "wish" | "diary" | "album",
  sourceId: string,
): Promise<CalendarEvent> {
  const res = await call<CalendarEvent>("POST", `/api/v1/calendar/events/${id}/link`, {
    body: { source_type: sourceType, source_id: sourceId },
  });
  if (!res.response.ok) throw await apiError(res, "CALENDAR_LINK_FAILED");
  if (!res.data) throw new ApiError(res.response.status, "CALENDAR_LINK_EMPTY", "關聯失敗");
  return res.data;
}

// ----- W58.1: the daily log and the cycle settings -----------------------

/**
 * The daily log entries in a date range.
 *
 * **範圍，不是一天。** 月曆格需要可見範圍裡的每一筆，而日詳情只需要它
 * 正在顯示的那一天 —— 一個端點服務兩者。
 */
export async function getCycleLogs(query: {
  startDate: string;
  endDate: string;
}): Promise<CycleLog[]> {
  const p = new URLSearchParams();
  p.set("start_date", query.startDate);
  p.set("end_date", query.endDate);
  const res = await call<CycleLog[]>("GET", `/api/v1/calendar/cycle/logs?${p.toString()}`);
  if (!res.response.ok) throw await apiError(res, "CALENDAR_CYCLE_LOGS_FAILED");
  return res.data ?? [];
}

/**
 * Write one day of the daily log.
 *
 * **Upsert** —— 同一對、同一個人、同一天只有一行，寫兩次是編輯。
 *
 * **W58.4 —— 不送 `owner_id`。** 「誰的身體」是服務器判定的事（這一對的
 * 週期歸屬人），client 送了會被 422 拒絕。
 */
export async function upsertCycleLog(
  date: string,
  input: CycleLogInput,
): Promise<CycleLog> {
  const res = await call<CycleLog>("PUT", `/api/v1/calendar/cycle/logs/${date}`, {
    body: input,
  });
  if (!res.response.ok) throw await apiError(res, "CALENDAR_CYCLE_LOG_FAILED");
  if (!res.data) throw new ApiError(res.response.status, "CALENDAR_CYCLE_LOG_EMPTY", "儲存失敗");
  return res.data;
}

/**
 * Remove one day of the daily log.
 *
 * **W58.4 —— 不送 `owner_id`。** 刪除的目標是「這一對的週期歸屬人的那一天」，
 * 那也是服務器判定的事。
 */
export async function deleteCycleLog(date: string): Promise<void> {
  const res = await call<null>(
    "DELETE",
    `/api/v1/calendar/cycle/logs/${date}`,
    { expectBody: false },
  );
  if (!res.response.ok) throw await apiError(res, "CALENDAR_CYCLE_LOG_DELETE_FAILED");
}

/**
 * This person's cycle settings.
 *
 * **沒有設定列時後端回傳預設值** —— 所以前端永遠拿到同一個形狀，
 * 不需要分支「這個人有沒有改過」。
 */
export async function getCycleSettings(): Promise<CycleSettings> {
  const res = await call<CycleSettings>("GET", "/api/v1/calendar/cycle/settings");
  if (!res.response.ok) throw await apiError(res, "CALENDAR_CYCLE_SETTINGS_FAILED");
  if (!res.data) {
    throw new ApiError(res.response.status, "CALENDAR_CYCLE_SETTINGS_EMPTY", "設定沒有資料");
  }
  return res.data;
}

/** Change this person's cycle settings. Only the sent fields are written. */
export async function updateCycleSettings(
  input: CycleSettingsInput,
): Promise<CycleSettings> {
  const res = await call<CycleSettings>("PUT", "/api/v1/calendar/cycle/settings", {
    body: input,
  });
  if (!res.response.ok) throw await apiError(res, "CALENDAR_CYCLE_SETTINGS_UPDATE_FAILED");
  if (!res.data) {
    throw new ApiError(res.response.status, "CALENDAR_CYCLE_SETTINGS_EMPTY", "設定沒有資料");
  }
  return res.data;
}
